'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef } from 'react';
import Typography from '@alltrails/core/components/Typography';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { useLazyGetListTrailsQuery } from '@alltrails/explore-service/redux/exploreServiceApi';
import { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import styles from './styles.module.scss';
import TrailSegmentsLoading from './TrailSegmentsLoading';

const TrailSegments = dynamic(() => import('./TrailSegments'), { ssr: false });

type TrailSegmentsSectionProps = {
  isAdminOrPLPUser?: boolean;
  segmentIds: number[];
};

const TrailSegmentsSection = ({ isAdminOrPLPUser, segmentIds }: TrailSegmentsSectionProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoaded = useRef(false);
  const isVisible = useIsVisible(containerRef);

  const [getTrails, { data, isError, isFetching, isLoading, isUninitialized }] = useLazyGetListTrailsQuery();

  useEffect(() => {
    if (isVisible && !hasLoaded.current) {
      hasLoaded.current = true;
      getTrails({
        additionalAttributesToRetrieve: ['polyline'],
        filters: { recordIds: [{ ids: segmentIds, mode: 'and', recordType: 'trail' }] },
        location: { center: { lat: 0, lng: 0 }, maxRadius: Number.MAX_SAFE_INTEGER }
      });
    }
  }, [getTrails, isVisible, segmentIds]);

  const content = useMemo(() => {
    if (isError) {
      return null;
    }

    if (isFetching || isLoading || isUninitialized) {
      return <TrailSegmentsLoading />;
    }

    if (data) {
      const sortedTrails = segmentIds.map(trailId => data.searchResults.find(trail => trail.ID === trailId)).filter(Boolean) as TrailSearchResult[];
      return <TrailSegments trails={sortedTrails} />;
    }

    return null;
  }, [data, isError, isFetching, isLoading, isUninitialized, segmentIds]);

  return content ? (
    <div className={classNames(styles.container, { [styles.admin]: isAdminOrPLPUser })} ref={containerRef}>
      <Typography variant="heading400">Trail segments</Typography>
      {content}
    </div>
  ) : null;
};

export default TrailSegmentsSection;
